/**
 * Wertia Dossier — Navbar orizzontale moderna (stile Apple/Linear).
 *
 * Design:
 *   - Sfondo bianco con bordo inferiore sottile
 *   - Tab pill: icona + label affiancate, padding generoso
 *   - Tab attivo: pill scuro con accent oro
 *   - Hover: sfondo grigio chiaro
 *   - Badge contatore inline accanto al label (es. "Spese · 4")
 *   - Niente numeri step, niente etichette gruppo, niente branding
 */
import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import {
  User, Wallet, Receipt, Building, BarChart3, Shield, Target, Download,
  ChevronLeft, ChevronRight,
} from "lucide-react";

export const DOSSIER_SECTIONS = [
  { path: "",                step: "1", icon: User,      label: "Dati cliente",      testid: "dossier-nav-dati-cliente" },
  { path: "salario",         step: "2", icon: Wallet,    label: "Reddito",           testid: "dossier-nav-salario" },
  { path: "spese-contratti", step: "3", icon: Receipt,   label: "Spese & Contratti", testid: "dossier-nav-spese-contratti", badgeKey: "contratti" },
  { path: "patrimonio",      step: "4", icon: Building,  label: "Patrimonio",        testid: "dossier-nav-patrimonio" },
  { path: "riepilogo",       step: "5", icon: BarChart3, label: "Riepilogo",         testid: "dossier-nav-riepilogo" },
  { path: "coperture",       step: "6", icon: Shield,    label: "Coperture",         testid: "dossier-nav-coperture" },
  { path: "strategia",       step: "7", icon: Target,    label: "Strategia",         testid: "dossier-nav-strategia" },
  { path: "export",          step: "·", icon: Download,  label: "Export",            testid: "dossier-nav-export" },
];

export default function DossierTopNav({ clientId, dossier }) {
  const base = `/clients/${clientId}/dossier`;

  return (
    <nav
      className="sticky top-0 z-30 bg-white border-b border-stone-200"
      data-testid="dossier-topnav"
    >
      <div className="max-w-[1600px] mx-auto px-4">
        <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
          {DOSSIER_SECTIONS.map((s) => {
            const Icon = s.icon;
            const badge = s.badgeKey === "contratti" ? dossier?.contratti?.length : null;
            const to = s.path ? `${base}/${s.path}` : base;
            return (
              <NavLink
                key={s.path}
                to={to}
                end
                className={({ isActive }) =>
                  `group inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? "bg-stone-900 text-amber-300 shadow-sm"
                      : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  }`
                }
                data-testid={s.testid}
                title={s.label}
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
                    <span>{s.label}</span>
                    {badge ? (
                      <span
                        className={`inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] leading-none ${
                          isActive
                            ? "bg-amber-300 text-stone-900"
                            : "bg-stone-200 text-stone-700 group-hover:bg-stone-300"
                        }`}
                      >
                        {badge}
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

/** Pulsanti "Precedente / Successiva" basati sull'URL corrente. */
export function PrevNextButtons({ clientId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const base = `/clients/${clientId}/dossier`;

  const m = location.pathname.match(new RegExp(`${base.replace(/\//g, "\\/")}\\/?([^/]*)`));
  const current = m ? (m[1] || "") : "";
  const idx = DOSSIER_SECTIONS.findIndex((s) => s.path === current);
  const prev = idx > 0 ? DOSSIER_SECTIONS[idx - 1] : null;
  const next = idx >= 0 && idx < DOSSIER_SECTIONS.length - 1 ? DOSSIER_SECTIONS[idx + 1] : null;

  return (
    <div className="flex items-center justify-between gap-3 pt-6 mt-6 border-t border-stone-200" data-testid="dossier-prevnext">
      <div className="flex-1">
        {prev ? (
          <Button
            variant="outline"
            onClick={() => navigate(prev.path ? `${base}/${prev.path}` : base)}
            className="gap-2"
            data-testid="dossier-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Precedente:</span>
            <span className="font-semibold">{prev.label}</span>
          </Button>
        ) : <div />}
      </div>
      <div className="text-[11px] text-stone-400 uppercase tracking-widest font-semibold">
        {idx >= 0 ? `${idx + 1} / ${DOSSIER_SECTIONS.length}` : ""}
      </div>
      <div className="flex-1 flex justify-end">
        {next ? (
          <Button
            onClick={() => navigate(next.path ? `${base}/${next.path}` : base)}
            className="gap-2 bg-prime text-prime-foreground hover:bg-prime/90"
            data-testid="dossier-next-btn"
          >
            <span className="hidden sm:inline">Successiva:</span>
            <span className="font-semibold">{next.label}</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        ) : <div />}
      </div>
    </div>
  );
}
